import React, { useEffect, useRef, useState } from "react";
import { SectionHead } from "./SectionParts";
import { Headphones, Play, Pause, Volume2 } from "lucide-react";

// Free public-domain audio samples used as fallback when an article lacks audio_url
const SAMPLE = [
  {
    id: "s0",
    title: "बोध पॉडकास्ट: डिजिटल भारत की दिशा",
    author: "Bodh Podcast",
    summary: "32:14",
    cover_image: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?crop=entropy&cs=srgb&fm=jpg&q=85",
    audio_url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
  },
  {
    id: "s1",
    title: "AI युग में पत्रकारिता",
    author: "विशेषज्ञ पैनल",
    summary: "45:01",
    cover_image: "https://images.unsplash.com/photo-1589903308904-1010c2294adc?crop=entropy&cs=srgb&fm=jpg&q=85",
    audio_url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
  },
  {
    id: "s2",
    title: "महिला सशक्तिकरण की कहानियाँ",
    author: "रचना सिंह",
    summary: "28:50",
    cover_image: "https://images.unsplash.com/photo-1573497019418-b400bb3ab074?crop=entropy&cs=srgb&fm=jpg&q=85",
    audio_url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3",
  },
];

function fmt(sec) {
  if (!sec || isNaN(sec)) return "00:00";
  const s = Math.floor(sec);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function PodcastCard({ item, index, isPlaying, onToggle, audioRef }) {
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      setCurrent(a.currentTime);
      setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0);
    };
    const onMeta = () => setDuration(a.duration || 0);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
    };
  }, [audioRef]);

  return (
    <div data-testid={`podcast-card-${item.id}`} className="relative rounded-2xl overflow-hidden group">
      <div className="absolute inset-0">
        <img src={item.cover_image} className="absolute inset-0 w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/55 to-black/30" />
      </div>
      <div className="relative p-6 flex flex-col gap-4 min-h-[260px]">
        <span className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-md text-white text-[10px] font-outfit font-black uppercase tracking-widest border border-white/20">
          <Headphones className="h-3 w-3" /> Episode {String(index + 1).padStart(2, "0")}
        </span>
        <h3 className="font-devanagari text-white font-bold text-xl leading-snug">{item.title}</h3>

        <audio
          ref={audioRef}
          src={item.audio_url}
          preload="metadata"
          data-testid={`podcast-audio-${item.id}`}
        />

        <div className="mt-auto space-y-3">
          {/* Progress bar */}
          <div className="h-1 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bodh-gradient transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex items-center justify-between text-[10px] text-white/70 font-outfit">
            <span data-testid={`podcast-time-${item.id}`}>{fmt(current)}</span>
            <span>{fmt(duration) !== "00:00" ? fmt(duration) : item.summary || "30:00"}</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onToggle}
              data-testid={`podcast-play-${index}`}
              aria-label={isPlaying ? "Pause" : "Play"}
              className="h-12 w-12 rounded-full bodh-gradient flex items-center justify-center text-white shadow-lg hover:scale-105 transition-transform"
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" fill="currentColor" stroke="none" />}
            </button>
            <div className="text-xs text-zinc-300 font-hind">
              <div className="font-bold text-white">{item.author}</div>
              <div className="flex items-center gap-1 mt-0.5 text-white/70">
                <Volume2 className="h-3 w-3" /> Audio Episode
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PodcastSection({ articles = [] }) {
  const [playingIdx, setPlayingIdx] = useState(null);

  // Pad with SAMPLE so we always show 3 cards
  const items = [...articles];
  for (let i = 0; items.length < 3 && i < SAMPLE.length; i++) {
    if (!items.find((x) => x.title === SAMPLE[i].title)) items.push(SAMPLE[i]);
  }
  const finalItems = items.slice(0, 3).map((p, i) => ({
    id: p.id || `s${i}`,
    title: p.title,
    author: p.author || "Bodh Podcast",
    summary: p.summary || "30:00",
    cover_image: p.cover_image || SAMPLE[i % SAMPLE.length].cover_image,
    audio_url: p.audio_url || SAMPLE[i % SAMPLE.length].audio_url,
  }));

  // One ref per card; we keep an array of refs to control playback exclusivity
  const refs = useRef([]);
  refs.current = finalItems.map((_, i) => refs.current[i] || React.createRef());

  const toggle = (i) => {
    const targetAudio = refs.current[i].current;
    if (!targetAudio) return;
    // Stop all others
    refs.current.forEach((r, idx) => {
      if (idx !== i && r.current) {
        r.current.pause();
        r.current.currentTime = 0;
      }
    });
    if (playingIdx === i) {
      targetAudio.pause();
      setPlayingIdx(null);
    } else {
      const p = targetAudio.play();
      if (p && p.catch) p.catch(() => {});
      setPlayingIdx(i);
    }
  };

  // Listen for natural end
  useEffect(() => {
    const handlers = refs.current.map((r, i) => {
      const fn = () => setPlayingIdx((cur) => (cur === i ? null : cur));
      if (r.current) r.current.addEventListener("ended", fn);
      return { r, fn };
    });
    return () => {
      handlers.forEach(({ r, fn }) => r.current && r.current.removeEventListener("ended", fn));
    };
    // eslint-disable-next-line
  }, [finalItems.length]);

  return (
    <section className="my-16" data-testid="podcast-section">
      <SectionHead title="Bodh पॉडकास्ट" subtitle="गहराई से सुनिए, सोच को बढाइए" icon={<Headphones className="h-4 w-4" />} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {finalItems.map((p, i) => (
          <PodcastCard
            key={p.id}
            item={p}
            index={i}
            isPlaying={playingIdx === i}
            onToggle={() => toggle(i)}
            audioRef={refs.current[i]}
          />
        ))}
      </div>
    </section>
  );
}
